/**
 * HDR post chain (spec phase-2 §7): exposure (eye adaptation) → bloom on bright lights only → AgX → anime grade LUT
 * (rebuilt when the sun moves > 0.5°) → vignette → faint grain; SMAA on `med`, MSAA ×4 on `high`.
 */
import * as THREE from 'three';
import {
  BlendFunction,
  BloomEffect,
  Effect,
  EffectComposer,
  EffectPass,
  LUT3DEffect,
  NoiseEffect,
  RenderPass,
  SMAAEffect,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from 'postprocessing';
import type { QualityLevel } from '../../app/urlParams';
import { buildLut3D, gradeForSunAlt, type Grade } from '../grade';

export const LUT_SIZE = 32;

class ExposureEffect extends Effect {
  constructor() {
    super(
      'ExposureEffect',
      /* glsl */ `uniform float exposure;
      void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
        outputColor = vec4(inputColor.rgb * exposure, inputColor.a);
      }`,
      { uniforms: new Map([['exposure', new THREE.Uniform(1)]]) },
    );
  }
  set exposure(v: number) {
    this.uniforms.get('exposure')!.value = v;
  }
}

/** A float RGBA 3D texture holding the grade LUT (red fastest, as buildLut3D lays it out). */
export function lutTexture(grade: Grade, size = LUT_SIZE): THREE.Data3DTexture {
  const tex = new THREE.Data3DTexture(buildLut3D(grade, size), size, size, size);
  tex.format = THREE.RGBAFormat;
  tex.type = THREE.FloatType;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = tex.wrapR = THREE.ClampToEdgeWrapping;
  tex.unpackAlignment = 1;
  tex.needsUpdate = true;
  return tex;
}

export class PostChain {
  readonly composer: EffectComposer;
  private readonly exposure = new ExposureEffect();
  private readonly bloom: BloomEffect;
  private readonly lut: LUT3DEffect;
  private readonly noise: NoiseEffect;
  private readonly smaaPass: EffectPass;
  private readonly mainPass: EffectPass;
  private lutSunAlt = Number.NaN;

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, quality: QualityLevel) {
    this.composer = new EffectComposer(renderer, { frameBufferType: THREE.HalfFloatType, multisampling: quality === 'high' ? 4 : 0 });
    this.composer.addPass(new RenderPass(scene, camera));
    this.bloom = new BloomEffect({ luminanceThreshold: 1.6, luminanceSmoothing: 0.35, mipmapBlur: true, intensity: 0.85, radius: 0.65 });
    this.lut = new LUT3DEffect(lutTexture(gradeForSunAlt(90)), { tetrahedralInterpolation: true });
    this.noise = new NoiseEffect({ premultiply: true, blendFunction: BlendFunction.SCREEN });
    this.noise.blendMode.opacity.value = 0.03;
    const vignette = new VignetteEffect({ darkness: 0.28, offset: 0.35 });
    this.composer.addPass(new EffectPass(camera, this.exposure));
    this.mainPass = new EffectPass(camera, this.bloom, new ToneMappingEffect({ mode: ToneMappingMode.AGX }), this.lut, vignette, this.noise);
    this.composer.addPass(this.mainPass);
    this.smaaPass = new EffectPass(camera, new SMAAEffect());
    this.composer.addPass(this.smaaPass);
    this.setQuality(quality);
  }

  setQuality(q: QualityLevel): void {
    // The composer only presents the LAST pass: when SMAA is off, the main pass must render to the screen itself.
    this.smaaPass.enabled = q === 'med';
    this.smaaPass.renderToScreen = true;
    this.mainPass.renderToScreen = !this.smaaPass.enabled;
    this.composer.multisampling = q === 'high' ? 4 : 0;
    this.noise.blendMode.opacity.value = q === 'low' ? 0 : 0.03;
    this.bloom.intensity = q === 'low' ? 0.6 : 0.85;
  }

  /** Rebuilds the grade LUT when the sun moved more than 0.5°; `force` after live grade edits. */
  update(exposure: number, sunAltDeg: number, force = false): void {
    this.exposure.exposure = exposure;
    if (force || !(Math.abs(sunAltDeg - this.lutSunAlt) <= 0.5)) {
      const old = this.lut.lut;
      this.lut.lut = lutTexture(gradeForSunAlt(sunAltDeg));
      old?.dispose();
      this.lutSunAlt = sunAltDeg;
    }
  }

  setSize(width: number, height: number): void {
    this.composer.setSize(width, height, false);
  }

  render(dtSec: number): void {
    this.composer.render(dtSec);
  }
}
